import * as React from 'react';
import { cn } from '../lib/cn';

/**
 * Switch and Checkbox — custom-drawn, native underneath.
 *
 * Both render a real `<input type="checkbox">` as the actual control and paint the visual on top
 * of it with a sibling `<span>`. That is the whole trick, and it is deliberate: the input keeps
 * focus, space/enter, form participation, `:disabled`, and the screen-reader contract, all of
 * which a hand-rolled `<div role="switch">` has to reimplement and usually gets at least one of
 * wrong. What we take back is the paint.
 *
 * The input is `opacity-0` and stretched over the control rather than `sr-only`. `sr-only` would
 * pull it out of the layout, and the layout is what the `peer-*` variants below key off — the tick
 * and the thumb are styled entirely from the input's state, so the input has to remain the peer
 * sitting under them.
 *
 * COLOURS. These live in @most/ui and render in four apps whose palettes disagree, so they are
 * built from the shadcn core tokens only. Notably the "on" fill is `--foreground` (ink), NOT
 * `--primary`: the admin's primary is a butter yellow that is illegible under a white checkmark
 * and reads as a warning, not a confirmation. Ink is correct on every palette in the monorepo.
 *
 * Where these get used tells you why they matter:
 *
 *   · Switch  → ADM-7's category `is_active` toggle. Flipping it OFF removes a category AND every
 *     product in it from the buyer catalog and from search, instantly. The call site wraps it in
 *     a confirm that names the product count.
 *
 *   · Checkbox → ADM-3's "confirm negative margin". The one deliberate speed-bump in the pricing
 *     calculator: Merdan is allowed to sell below cost, but not by accident.
 */

export type SwitchProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
};

export function Switch({
  checked,
  onCheckedChange,
  disabled,
  id,
  className,
  ...aria
}: SwitchProps) {
  return (
    <span
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full',
        'transition-colors duration-200 ease-out',
        // The track. Ink when on, muted when off — see the colour note above.
        checked ? 'bg-foreground' : 'bg-input',
        // Disabled has to be legible at a GLANCE, and opacity alone was not doing it: a 50%-opacity
        // near-black track still reads as a confident dark "on" switch. Desaturating to a flat grey
        // is what actually says "you cannot touch this", so an admin does not sit there clicking a
        // category toggle that is never going to move.
        disabled && (checked ? 'bg-muted-foreground/40' : 'bg-input/50'),
        className,
      )}
    >
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className={cn(
          'peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer appearance-none rounded-full opacity-0',
          'disabled:cursor-not-allowed',
        )}
        {...aria}
      />

      {/* The thumb. `peer-focus-visible` puts the ring on the TRACK's shape, so a keyboard user
          sees the whole switch light up rather than a ring around an invisible box. */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute left-0.5 h-5 w-5 rounded-full bg-background shadow-sm',
          'transition-transform duration-200 ease-out',
          checked && 'translate-x-5',
          disabled && 'bg-background/70 shadow-none',
        )}
      />
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -inset-px rounded-full ring-ring ring-offset-2 ring-offset-background',
          'peer-focus-visible:ring-2',
        )}
      />
    </span>
  );
}

export type CheckboxProps = {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
};

export function Checkbox({
  checked,
  onCheckedChange,
  disabled,
  id,
  className,
  ...aria
}: CheckboxProps) {
  return (
    <span className={cn('relative inline-flex h-[18px] w-[18px] shrink-0', className)}>
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onCheckedChange(e.target.checked)}
        className={cn(
          'peer absolute inset-0 z-10 m-0 h-full w-full cursor-pointer appearance-none rounded-[5px] opacity-0',
          'disabled:cursor-not-allowed',
        )}
        {...aria}
      />

      {/* The box. Drawn from the input's state, so hover/focus/disabled all come for free. */}
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 flex items-center justify-center rounded-[5px] border',
          'transition-colors duration-150',
          checked ? 'border-foreground bg-foreground' : 'border-input bg-background',
          !disabled && !checked && 'peer-hover:border-muted-foreground',
          'peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-background',
          'peer-disabled:opacity-50',
        )}
      >
        {/* The tick. `pathLength` + a dash offset animate it as a STROKE being drawn rather than a
            shape fading in — the motion tracks the gesture, which is the one place a work screen
            earns an animation. */}
        <svg
          viewBox="0 0 16 16"
          fill="none"
          className={cn(
            'h-[14px] w-[14px] text-background transition-opacity duration-150',
            checked ? 'opacity-100' : 'opacity-0',
          )}
        >
          <path
            d="M3.5 8.5l3 3 6-6.5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength={1}
            className={cn(
              '[stroke-dasharray:1] transition-[stroke-dashoffset] duration-200 ease-out',
              checked ? '[stroke-dashoffset:0]' : '[stroke-dashoffset:1]',
            )}
          />
        </svg>
      </span>
    </span>
  );
}

/**
 * Tooltip — a title-attribute shim.
 *
 * The admin app composes `<TooltipProvider>` at the root, so the export has to exist. But a real
 * tooltip (portal, positioning, hover-intent, touch fallback) is a lot of machinery for something
 * that, in this product, is only ever used to explain a table header. The native `title` does
 * that, works on keyboard focus, and costs nothing.
 *
 * Anything that MUST be readable — a status, a warning, a price — belongs in the layout, not
 * behind a hover. Tooltips are for the nice-to-know.
 */
export function TooltipProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export function Tooltip({
  content,
  children,
}: {
  content: string;
  children: React.ReactNode;
}) {
  return <span title={content}>{children}</span>;
}
